import type { StoreProviderId } from "../../features/seller/stores/store-providers";
import type { LocalizedDict } from "../index";

type PlatformGuide = {
  description: string;
  highlights: [string, string, string];
  setup: string;
  cost: string;
  subscription: string;
};

export type SellerStoresStrings = {
  page: {
    emptyTitle: string;
    connectedTitle: string;
    alreadyHave: string;
    anotherStore: string;
    connectDescription: string;
    allIntegrations: string;
    noStore: string;
    noStoreDescription: string;
    migrationTitle: string;
    migrationDescription: string;
  };
  actions: {
    connect: string;
    comingSoon: string;
    listProduct: string;
    listFirstProduct: string;
    skip: string;
    learnMore: string;
    contactSupport: string;
    showMore: string;
    showLess: string;
    cancel: string;
    addStore: string;
    viewStore: string;
    editStoreDetails: string;
    reconnect: string;
    disconnect: string;
    disconnectConfirmTitle: string;
    disconnectConfirmBody: string;
    disconnectConfirmAction: string;
    reconnectUnavailable: string;
    settings: string;
    syncProducts: string;
    addProduct: string;
    manage: string;
    showDetails: (storeName: string) => string;
    hideDetails: (storeName: string) => string;
    enableOrderIntake: (storeName: string) => string;
    disableOrderIntake: (storeName: string) => string;
    moreActions: (storeName: string) => string;
  };
  status: {
    active: string;
    inactive: string;
    connected: string;
    notConnected: string;
    receivingOrders: string;
    notReceivingOrders: string;
    recommended: string;
  };
  group: {
    regionLabel: (providerName: string) => string;
    storeCount: (count: number) => string;
    needsAttention: (count: number) => string;
    allOperational: string;
    expand: (providerName: string) => string;
    collapse: (providerName: string) => string;
    connection: string;
    authorizationActive: string;
    authorizationRequired: string;
    lastSync: (value: string) => string;
    orderIntake: string;
    orderIntakeDescription: string;
    on: string;
    off: string;
    updating: string;
    arming: string;
    actionRecommended: string;
    storeLabel: (storeName: string) => string;
  };
  success: {
    title: string;
    description: string;
    fallbackStore: string;
  };
  detail: {
    backToStores: string;
    loading: string;
    loadError: string;
    notFoundTitle: string;
    notFoundDescription: string;
  };
  extension: {
    title: string;
    description: string;
    working: (seen: string) => string;
    quiet: (seen: string) => string;
    waiting: string;
    off: string;
    create: string;
    replace: string;
    turnOff: string;
    turnOffConfirm: string;
    turnOffAction: string;
    cancel: string;
    codeTitle: string;
    codeHint: string;
    copy: string;
    copied: string;
    copyFailed: string;
    done: string;
    created: string;
    turnedOff: string;
    actionFailed: string;
    install: {
      title: string;
      download: string;
      unzip: string;
      developerMode: string;
      loadUnpacked: string;
      pasteCode: string;
      keepOpen: string;
    };
  };
  loading: string;
  loadError: string;
  retry: string;
  providerDescriptions: Record<StoreProviderId, string>;
  guide: Record<"shopify" | "etsy" | "amazon", PlatformGuide>;
  labels: {
    highlights: string;
    setup: string;
    cost: string;
    subscription: string;
  };
};

export const sellerStoresI18n: LocalizedDict<SellerStoresStrings> = {
  en: {
    page: {
      emptyTitle: "Connect your store to get started",
      connectedTitle: "Your stores",
      alreadyHave: "Already have a store?",
      anotherStore: "Connect another store",
      connectDescription:
        "Connect Pexify to your sales channels to sync listings and automate order intake.",
      allIntegrations: "More sales channels",
      noStore: "Don't have a store yet?",
      noStoreDescription:
        "Compare the sales platforms that best fit the way you want to sell.",
      migrationTitle: "Need help moving your products?",
      migrationDescription:
        "Use Pexify resources and support to prepare your catalog for a new sales channel.",
    },
    actions: {
      connect: "Connect",
      comingSoon: "Coming soon",
      listProduct: "List product",
      listFirstProduct: "List your first product",
      skip: "Skip for now",
      learnMore: "Learn more",
      contactSupport: "Open resources",
      showMore: "Show more",
      showLess: "Show less",
      cancel: "Cancel",
      addStore: "Add store",
      viewStore: "View store",
      editStoreDetails: "Edit store details",
      reconnect: "Reconnect",
      disconnect: "Disconnect store",
      disconnectConfirmTitle: "Disconnect this store?",
      disconnectConfirmBody:
        "Order intake stops and the store leaves this list. You can reconnect it later.",
      disconnectConfirmAction: "Disconnect",
      reconnectUnavailable: "Reconnect is not available for this channel yet.",
      settings: "Settings",
      syncProducts: "Sync products",
      addProduct: "Add product",
      manage: "Manage",
      showDetails: (storeName) => `Show details for ${storeName}`,
      hideDetails: (storeName) => `Hide details for ${storeName}`,
      enableOrderIntake: (storeName) => `Enable order intake for ${storeName}`,
      disableOrderIntake: (storeName) =>
        `Disable order intake for ${storeName}`,
      moreActions: (storeName) => `More actions for ${storeName}`,
    },
    status: {
      active: "Active",
      inactive: "Inactive",
      connected: "Connected",
      notConnected: "Not connected",
      receivingOrders: "Receiving orders",
      notReceivingOrders: "Not receiving orders",
      recommended: "Recommended",
    },
    group: {
      regionLabel: (providerName) => `${providerName} stores`,
      storeCount: (count) =>
        `${count} ${count === 1 ? "store" : "stores"} · grouped by platform`,
      needsAttention: (count) => `${count} need attention`,
      allOperational: "All operational",
      expand: (providerName) => `Expand ${providerName} stores`,
      collapse: (providerName) => `Collapse ${providerName} stores`,
      connection: "Connection",
      authorizationActive: "Authorization active",
      authorizationRequired: "Reconnect required",
      lastSync: (value) => `Last sync ${value}`,
      orderIntake: "Order intake",
      orderIntakeDescription: "Automatically send new store orders to Pexify.",
      on: "On",
      off: "Off",
      updating: "Updating…",
      arming: "Awaiting confirm",
      actionRecommended: "Action recommended",
      storeLabel: (storeName) => `${storeName} store`,
    },
    success: {
      title: "Your store is connected successfully!",
      description:
        "Take the next step: list your first Pexify product in this store and get closer to your first sale.",
      fallbackStore: "Connected store",
    },
    detail: {
      backToStores: "Stores",
      loading: "Loading store workspace…",
      loadError: "We could not load this store.",
      notFoundTitle: "Store not found",
      notFoundDescription:
        "This store is unavailable or does not belong to your organization.",
    },
    extension: {
      title: "Browser extension",
      description:
        "Fills in buyer addresses and marks orders shipped on Etsy from your own browser. Keep an Etsy Orders tab open in Chrome.",
      working: (seen) => `Working · last check-in ${seen}`,
      quiet: (seen) =>
        `Last check-in ${seen} — open Etsy Orders in Chrome to resume`,
      waiting: "Code created — waiting for the extension's first check-in",
      off: "Not set up",
      create: "Create connection code",
      replace: "Create new code",
      turnOff: "Turn off",
      turnOffConfirm: "The extension stops at its next check-in.",
      turnOffAction: "Turn off extension",
      cancel: "Cancel",
      codeTitle: "Your connection code",
      codeHint:
        "Paste it into the Pexify extension. It is shown only once; a new code replaces this one.",
      copy: "Copy",
      copied: "Copied",
      copyFailed: "Could not copy — select the code and copy it manually",
      done: "Done",
      created: "Connection code created",
      turnedOff: "Extension turned off",
      actionFailed: "Could not update the extension",
      install: {
        title: "Install the extension in Chrome",
        download: "Download the extension",
        unzip: "and unzip it. Keep the folder: Chrome runs the extension from it.",
        developerMode: "In Chrome, open chrome://extensions and turn on Developer mode (top right).",
        loadUnpacked: "Click Load unpacked and choose the unzipped pexify-etsy-helper folder.",
        pasteCode: "Create a connection code here, click the Pexify icon in Chrome's toolbar, paste the code and press Save and connect.",
        keepOpen: "Open Etsy's Orders & Shipping page and keep it open.",
      },
    },
    loading: "Loading your stores…",
    loadError: "We could not load your stores.",
    retry: "Try again",
    providerDescriptions: {
      etsy: "Reach marketplace shoppers with listings, orders, and reviews in sync.",
      shopify:
        "Run your own branded storefront with unified products and orders.",
      amazon: "Connect Seller Central for marketplace order intake.",
      woocommerce: "Bring a WordPress commerce store into Pexify.",
      tiktok: "Sell through TikTok Shop and route orders through Pexify.",
      wix: "Connect a Wix storefront to your catalog and fulfillment flow.",
      squarespace: "Sync a Squarespace commerce storefront.",
      bigcommerce: "Connect a BigCommerce storefront and its orders.",
      ebay: "Manage eBay listings and orders from Pexify.",
    },
    guide: {
      shopify: {
        description:
          "A flexible choice for building your own branded online store.",
        highlights: [
          "Customizable storefront",
          "Integrated checkout",
          "Large app ecosystem",
        ],
        setup: "Easy",
        cost: "$$$",
        subscription: "Yes",
      },
      etsy: {
        description:
          "A fast way to start selling products to an existing marketplace audience.",
        highlights: [
          "Built-in shopper traffic",
          "Simple listing workflow",
          "International reach",
        ],
        setup: "Easy",
        cost: "$$",
        subscription: "No",
      },
      amazon: {
        description:
          "A broad marketplace for sellers ready to operate at larger scale.",
        highlights: [
          "Large customer base",
          "Advertising tools",
          "Seller analytics",
        ],
        setup: "Medium",
        cost: "$$$",
        subscription: "Yes",
      },
    },
    labels: {
      highlights: "Highlights",
      setup: "Setup",
      cost: "Cost",
      subscription: "Subscription",
    },
  },
  tr: {
    page: {
      emptyTitle: "Başlamak için mağazanı bağla",
      connectedTitle: "Mağazaların",
      alreadyHave: "Zaten bir mağazan var mı?",
      anotherStore: "Başka bir mağaza bağla",
      connectDescription:
        "İlanları senkronlamak ve sipariş akışını otomatikleştirmek için satış kanallarını Pexify’a bağla.",
      allIntegrations: "Diğer satış kanalları",
      noStore: "Henüz mağazan yok mu?",
      noStoreDescription: "Satış şekline en uygun platformları karşılaştır.",
      migrationTitle: "Ürünlerini taşımak için yardıma mı ihtiyacın var?",
      migrationDescription:
        "Kataloğunu yeni bir satış kanalına hazırlamak için Pexify kaynaklarını ve desteğini kullan.",
    },
    actions: {
      connect: "Bağla",
      comingSoon: "Yakında",
      listProduct: "Ürün listele",
      listFirstProduct: "İlk ürününü listele",
      skip: "Şimdilik geç",
      learnMore: "Daha fazla bilgi",
      contactSupport: "Kaynakları aç",
      showMore: "Daha fazla göster",
      showLess: "Daha az göster",
      cancel: "İptal",
      addStore: "Mağaza ekle",
      viewStore: "Mağazayı görüntüle",
      editStoreDetails: "Mağaza bilgilerini düzenle",
      reconnect: "Yeniden bağlan",
      disconnect: "Bağlantıyı kes",
      disconnectConfirmTitle: "Bu mağazanın bağlantısı kesilsin mi?",
      disconnectConfirmBody:
        "Sipariş girişi durur ve mağaza bu listeden çıkar. İleride yeniden bağlayabilirsin.",
      disconnectConfirmAction: "Bağlantıyı kes",
      reconnectUnavailable: "Bu kanal için yeniden bağlanma henüz mevcut değil.",
      settings: "Ayarlar",
      syncProducts: "Ürünleri senkronla",
      addProduct: "Ürün ekle",
      manage: "Yönet",
      showDetails: (storeName) => `${storeName} ayrıntılarını göster`,
      hideDetails: (storeName) => `${storeName} ayrıntılarını gizle`,
      enableOrderIntake: (storeName) => `${storeName} için sipariş akışını aç`,
      disableOrderIntake: (storeName) =>
        `${storeName} için sipariş akışını kapat`,
      moreActions: (storeName) => `${storeName} için diğer işlemler`,
    },
    status: {
      active: "Aktif",
      inactive: "Pasif",
      connected: "Bağlı",
      notConnected: "Bağlı değil",
      receivingOrders: "Sipariş alıyor",
      notReceivingOrders: "Sipariş almıyor",
      recommended: "Önerilen",
    },
    group: {
      regionLabel: (providerName) => `${providerName} mağazaları`,
      storeCount: (count) => `${count} mağaza · platforma göre gruplandı`,
      needsAttention: (count) => `${count} mağaza ilgi bekliyor`,
      allOperational: "Tümü çalışıyor",
      expand: (providerName) => `${providerName} mağazalarını aç`,
      collapse: (providerName) => `${providerName} mağazalarını daralt`,
      connection: "Bağlantı",
      authorizationActive: "Yetkilendirme aktif",
      authorizationRequired: "Yeniden bağlamak gerekiyor",
      lastSync: (value) => `Son senkronizasyon ${value}`,
      orderIntake: "Sipariş akışı",
      orderIntakeDescription:
        "Yeni mağaza siparişlerini otomatik olarak Pexify’a aktar.",
      on: "Açık",
      off: "Kapalı",
      updating: "Güncelleniyor…",
      arming: "Onay bekliyor",
      actionRecommended: "İşlem öneriliyor",
      storeLabel: (storeName) => `${storeName} mağazası`,
    },
    success: {
      title: "Mağazan başarıyla bağlandı!",
      description:
        "Sıradaki adımı at: ilk Pexify ürününü bu mağazada listele ve ilk satışına yaklaş.",
      fallbackStore: "Bağlı mağaza",
    },
    detail: {
      backToStores: "Mağazalar",
      loading: "Mağaza çalışma alanı yükleniyor…",
      loadError: "Bu mağazayı yükleyemedik.",
      notFoundTitle: "Mağaza bulunamadı",
      notFoundDescription:
        "Bu mağaza kullanılamıyor veya kuruluşuna ait değil.",
    },
    extension: {
      title: "Tarayıcı eklentisi",
      description:
        "Alıcı adreslerini doldurur ve siparişleri Etsy'de kendi tarayıcından kargoya verildi yapar. Chrome'da bir Etsy Orders sekmesi açık kalsın.",
      working: (seen) => `Çalışıyor · son bağlantı ${seen}`,
      quiet: (seen) =>
        `Son bağlantı ${seen} — devam etmesi için Chrome'da Etsy Orders'ı aç`,
      waiting: "Kod oluşturuldu — eklentinin ilk bağlantısı bekleniyor",
      off: "Kurulmadı",
      create: "Bağlantı kodu oluştur",
      replace: "Yeni kod oluştur",
      turnOff: "Kapat",
      turnOffConfirm: "Eklenti bir sonraki bağlantısında durur.",
      turnOffAction: "Eklentiyi kapat",
      cancel: "Vazgeç",
      codeTitle: "Bağlantı kodun",
      codeHint:
        "Pexify eklentisine yapıştır. Yalnız bir kez gösterilir; yeni kod bunun yerini alır.",
      copy: "Kopyala",
      copied: "Kopyalandı",
      copyFailed: "Kopyalanamadı — kodu seçip elle kopyala",
      done: "Tamam",
      created: "Bağlantı kodu oluşturuldu",
      turnedOff: "Eklenti kapatıldı",
      actionFailed: "Eklenti güncellenemedi",
      install: {
        title: "Eklentiyi Chrome'a kur",
        download: "Eklentiyi indir",
        unzip: "ve zip dosyasını aç. Klasörü silme: Chrome eklentiyi oradan çalıştırır.",
        developerMode: "Chrome'da chrome://extensions adresini aç ve sağ üstteki Geliştirici modu'nu aç.",
        loadUnpacked: "Paketlenmemiş öğe yükle'ye bas ve açtığın pexify-etsy-helper klasörünü seç.",
        pasteCode: "Burada bir bağlantı kodu oluştur, Chrome araç çubuğundaki Pexify simgesine tıkla, kodu yapıştır ve Save and connect'e bas.",
        keepOpen: "Etsy'de Orders & Shipping sayfasını aç ve açık bırak.",
      },
    },
    loading: "Mağazaların yükleniyor…",
    loadError: "Mağazalarını yükleyemedik.",
    retry: "Tekrar dene",
    providerDescriptions: {
      etsy: "İlanları, siparişleri ve yorumları senkronlayarak pazar yeri müşterilerine ulaş.",
      shopify:
        "Ürün ve siparişleri tek yerde yönetirken kendi markalı mağazanı işlet.",
      amazon: "Pazar yeri sipariş akışı için Seller Central hesabını bağla.",
      woocommerce: "WordPress tabanlı mağazanı Pexify’a getir.",
      tiktok: "TikTok Shop üzerinden sat ve siparişleri Pexify’a yönlendir.",
      wix: "Wix mağazanı katalog ve fulfillment akışına bağla.",
      squarespace: "Squarespace Commerce mağazanı senkronla.",
      bigcommerce: "BigCommerce mağazanı ve siparişlerini bağla.",
      ebay: "eBay ilanlarını ve siparişlerini Pexify’dan yönet.",
    },
    guide: {
      shopify: {
        description:
          "Kendi markalı online mağazanı kurmak için esnek bir seçenek.",
        highlights: [
          "Özelleştirilebilir mağaza",
          "Entegre ödeme",
          "Geniş uygulama ekosistemi",
        ],
        setup: "Kolay",
        cost: "$$$",
        subscription: "Evet",
      },
      etsy: {
        description:
          "Hazır bir pazar yeri kitlesine hızlıca ürün satmaya başlamanın yolu.",
        highlights: [
          "Hazır müşteri trafiği",
          "Basit ilan akışı",
          "Uluslararası erişim",
        ],
        setup: "Kolay",
        cost: "$$",
        subscription: "Hayır",
      },
      amazon: {
        description:
          "Daha büyük ölçekte çalışmaya hazır satıcılar için geniş bir pazar yeri.",
        highlights: [
          "Geniş müşteri tabanı",
          "Reklam araçları",
          "Satıcı analizleri",
        ],
        setup: "Orta",
        cost: "$$$",
        subscription: "Evet",
      },
    },
    labels: {
      highlights: "Öne çıkanlar",
      setup: "Kurulum",
      cost: "Maliyet",
      subscription: "Abonelik",
    },
  },
};
